/**
 * URL synchronization utilities for map location sharing
 * Allows syncing map ID, viewport position (x, y), zoom level, layer states, and UI options to URL params
 */

/**
 * Layer state override from URL (visibility and opacity)
 */
export interface LayerOverride {
  id: string;
  visible?: boolean;
  opacity?: number;
}

/**
 * Parsed URL parameters for map navigation
 */
export interface UrlParams {
  mapId: string | null;
  x: number | null;
  y: number | null;
  zoom: number | null;
  /** Layer overrides for visibility and opacity */
  layerOverrides: LayerOverride[];
  /** Layer ID with open description panel */
  descriptionLayerId: string | null;
  /** Hide all UI elements (viewer-only mode) */
  hideUi: boolean;
}

/**
 * Layer state for URL sync
 */
export interface LayerUrlState {
  id: string;
  visible: boolean;
  opacity: number;
}

/**
 * Parses URL search parameters to extract map navigation data
 * @returns Parsed URL parameters with map ID and viewport info
 */
export function parseUrlParams(): UrlParams {
  const params = new URLSearchParams(window.location.search);

  const mapId = params.get('map');
  const xParam = params.get('x');
  const yParam = params.get('y');
  const zoomParam = params.get('z');

  // Parse hidden layers (comma-separated layer IDs)
  const hiddenParam = params.get('hidden');
  const hiddenLayerIds = hiddenParam ? hiddenParam.split(',').filter(id => id.length > 0) : [];

  // Parse layer opacities (format: "layerId:opacity,layerId:opacity")
  const opacityParam = params.get('opacity');
  const opacityMap = new Map<string, number>();
  if (opacityParam) {
    const pairs = opacityParam.split(',');
    for (const pair of pairs) {
      const [layerId, opacityStr] = pair.split(':');
      if (layerId && opacityStr) {
        const opacity = parseInt(opacityStr, 10);
        if (!isNaN(opacity) && opacity >= 0 && opacity <= 100) {
          opacityMap.set(layerId, opacity);
        }
      }
    }
  }

  // Build layer overrides from hidden and opacity params
  const layerOverrides: LayerOverride[] = [];
  const processedIds = new Set<string>();

  // Add hidden layers
  for (const id of hiddenLayerIds) {
    layerOverrides.push({
      id,
      visible: false,
      opacity: opacityMap.get(id)
    });
    processedIds.add(id);
  }

  // Add layers with opacity overrides that weren't hidden
  for (const [id, opacity] of opacityMap) {
    if (!processedIds.has(id)) {
      layerOverrides.push({ id, opacity });
    }
  }

  // Parse description panel layer ID
  const descriptionLayerId = params.get('desc');

  // Parse hideui flag
  const hideUi = params.get('hideui') !== null;

  return {
    mapId,
    x: xParam !== null ? parseFloat(xParam) : null,
    y: yParam !== null ? parseFloat(yParam) : null,
    zoom: zoomParam !== null ? parseFloat(zoomParam) : null,
    layerOverrides,
    descriptionLayerId,
    hideUi
  };
}

/**
 * Updates the URL with current map and viewport state without page reload
 * Uses history.replaceState to avoid polluting browser history
 * @param mapId - Current map identifier
 * @param x - Viewport X position in world coordinates
 * @param y - Viewport Y position in world coordinates
 * @param zoom - Viewport zoom level (screen pixels per world unit)
 * @param layers - Optional layer states for visibility and opacity
 * @param descriptionLayerId - Optional layer ID with open description panel
 */
export function updateUrl(
  mapId: string,
  x: number,
  y: number,
  zoom: number,
  layers?: LayerUrlState[],
  descriptionLayerId?: string | null
): void {
  const params = new URLSearchParams(window.location.search);

  params.set('map', mapId);
  // Round coordinates to avoid excessively long URLs
  params.set('x', Math.round(x).toString());
  params.set('y', Math.round(y).toString());
  // Round zoom to 6 decimal places for precision without excessive length
  params.set('z', parseFloat(zoom.toPrecision(6)).toString());

  // Update layer visibility (hidden layers)
  if (layers) {
    const hiddenLayers = layers.filter(l => !l.visible).map(l => l.id);
    if (hiddenLayers.length > 0) {
      params.set('hidden', hiddenLayers.join(','));
    } else {
      params.delete('hidden');
    }

    // Update layer opacities (only non-100% values)
    const opacityPairs = layers
      .filter(l => l.opacity !== 100)
      .map(l => `${l.id}:${l.opacity}`);
    if (opacityPairs.length > 0) {
      params.set('opacity', opacityPairs.join(','));
    } else {
      params.delete('opacity');
    }
  }

  // Update description panel state
  if (descriptionLayerId !== undefined) {
    if (descriptionLayerId) {
      params.set('desc', descriptionLayerId);
    } else {
      params.delete('desc');
    }
  }

  // Preserve hideui param if it exists
  // (hideui is only added manually, not by the app)

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState(null, '', newUrl);
}

/**
 * Clears map-related URL parameters
 * Used when no map is selected
 */
export function clearUrlParams(): void {
  window.history.replaceState(null, '', window.location.pathname);
}

/**
 * Creates a debounced version of updateUrl to prevent excessive URL updates
 * during rapid viewport changes (panning/zooming)
 * @param delay - Debounce delay in milliseconds
 * @returns Debounced update function
 */
export function createDebouncedUrlUpdate(delay: number = 300): (
  mapId: string,
  x: number,
  y: number,
  zoom: number,
  layers?: LayerUrlState[],
  descriptionLayerId?: string | null
) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (
    mapId: string,
    x: number,
    y: number,
    zoom: number,
    layers?: LayerUrlState[],
    descriptionLayerId?: string | null
  ): void => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      updateUrl(mapId, x, y, zoom, layers, descriptionLayerId);
      timeoutId = null;
    }, delay);
  };
}
